import { Alert, Button, Form } from 'antd';
import { PhoneCall } from 'lucide-react';
import { Link } from 'react-router';
import { driverTabPath } from '../../drivers/drivers.paths';
import { expenseTabPath } from '../../expenses/expenses.paths';
import { useDriverCaptureForm } from '../hooks/use-driver-capture-form';
import type { CaptureFormValues } from '../policies/capture-step.policy';
import { DriverCaptureFields } from './DriverCaptureFields';
import { DriverCaptureList } from './DriverCaptureList';

/**
 * One form for every step the driver's phone can report, for when the phone is dead, lost or full (gitaumoses4/chukua#79). It writes what the
 * phone would have written: same commands, same validation, the driver being the trip's own. Enter saves, and focus goes back to the step picker.
 */
export function DriverCapturePanel({ tripId, driver }: { tripId: string; driver: { id: string; label: string } }) {
  const capture = useDriverCaptureForm(tripId, driver.label);
  return (
    <section aria-label="Record for the driver" className="rounded-lg border border-line bg-surface p-5">
      <h2 className="flex items-center gap-2 text-base font-semibold text-fg">
        <PhoneCall className="size-5 text-muted" aria-hidden /> Record for {driver.label}
      </h2>
      <p className="mb-4 text-sm text-muted">
        Phone lost, dead or full? Record the step here as the driver would. Revoke the phone on the{' '}
        <Link className="text-primary underline" to={driverTabPath(driver.id, 'devices')}>
          driver&apos;s Devices tab
        </Link>
        . For a cost,{' '}
        <Link className="text-primary underline" to={expenseTabPath('all')}>
          record an expense
        </Link>{' '}
        and choose this trip&apos;s driver.
      </p>
      {capture.error && <Alert className="mb-4" type="error" title={capture.error} showIcon />}
      <Form<CaptureFormValues>
        form={capture.form}
        layout="vertical"
        requiredMark={false}
        initialValues={capture.initialValues}
        onFinish={capture.submit}
        disabled={capture.isSubmitting}
      >
        <DriverCaptureFields step={capture.step} picker={capture.picker} photoInputKey={capture.photoInputKey} onPhotos={capture.setPhotos} />
        <Form.Item className="mb-0">
          <Button type="primary" htmlType="submit" loading={capture.isSubmitting}>
            Record step
          </Button>
        </Form.Item>
      </Form>
      <h3 className="mt-6 mb-2 text-sm font-semibold text-fg">Recorded for this trip</h3>
      <DriverCaptureList state={capture.state} />
    </section>
  );
}
